// src/components/AssessmentSummaryCard.tsx
// The "auto-summary" glanceable block — one card combining every field an
// Assessment Module marked contributes_to_summary, its due-checklist
// screening statuses, and (if the module defines composite scoring_rules)
// an overall Green/Yellow/Red. Used live in Pre-Consult as the nurse/PA
// fills the form, and read-only wherever a saved assessment needs replaying
// (Consultant view, Patient Records).
import { RiskBadge, TRIAGE_TONE } from './ui/RiskBadge';

export type TriageColor = 'green' | 'yellow' | 'red';

export interface SummaryField {
  key: string;
  label: string;
  color: TriageColor;
  value: string | number | string[] | null | undefined;
}

export interface DueChecklistItem {
  label: string;
  color: TriageColor;
  last_done?: string | null;
}

/** backend/utils/assessmentScoring.js's computed summary — per-field
 * green/yellow/red dots, due-checklist statuses, and (if the template
 * defines scoring_rules) a composite overall verdict. */
export interface AssessmentSummary {
  fields?: SummaryField[];
  due_checklist?: DueChecklistItem[];
  overall?: TriageColor | null;
}

// Colors reference the same theme.css tokens RiskBadge/Badge use (as CSS
// vars, so this stays legacy-inline-style but still repaints with hospital
// branding and dark mode) rather than a locally hardcoded hex pair.
const TONE_META: Record<TriageColor, { bg: string; border: string; dot: string; label: string }> = {
  green:  { bg:'var(--color-success-soft)', border:'var(--color-success)', dot:'var(--color-success-ink)', label:'Green' },
  yellow: { bg:'var(--color-warn-soft)',    border:'var(--color-warn)',    dot:'var(--color-warn-ink)',    label:'Yellow' },
  red:    { bg:'var(--color-danger-soft)',  border:'var(--color-danger)',  dot:'var(--color-danger-ink)',  label:'Red' },
};

function Dot({ color }: { color?: TriageColor }) {
  const meta = color ? TONE_META[color] : null;
  return <span style={{ display:'inline-block', width:9, height:9, borderRadius:'50%', background: meta ? meta.dot : 'var(--color-line)' }} />;
}

export interface AssessmentSummaryCardProps {
  summary: AssessmentSummary | null | undefined;
  title?: string;
  /** Every caller (ConsultStage.tsx, PreConsultStage.tsx) wires this to
   * collapse back to the thin summary bar this card expanded from — never
   * to navigate to "more" — so the button it renders says "Fewer", not
   * "View full" (which was backwards: shown only once you're already
   * looking at the full card). */
  onViewFull?: () => void;
}

export default function AssessmentSummaryCard({ summary, title = 'Auto summary', onViewFull }: AssessmentSummaryCardProps) {
  if (!summary) return null;
  const { fields = [], due_checklist = [], overall } = summary;
  if (!fields.length && !due_checklist.length && !overall) return null;
  const overallMeta = overall ? TONE_META[overall] : null;

  return (
    <div className="fade-in" style={{
      marginBottom: 16, padding: '14px 16px', borderRadius: 'var(--radius)',
      background: overallMeta ? overallMeta.bg : 'var(--color-muted)',
      border: `1px solid ${overallMeta ? overallMeta.border : 'var(--color-line)'}`,
    }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10, gap:8, flexWrap:'wrap' }}>
        <div style={{ fontSize:11.5, fontWeight:700, letterSpacing:'.5px', color:'var(--color-ink-muted)', textTransform:'uppercase' }}>{title}</div>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          {overall && overallMeta && <RiskBadge tone={TRIAGE_TONE[overall]}>Overall: {overallMeta.label}</RiskBadge>}
          {onViewFull && <button className="btn-ghost" style={{ fontSize:11, padding:'3px 9px' }} onClick={onViewFull}>▾ Fewer</button>}
        </div>
      </div>

      {fields.length > 0 && (
        <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom: due_checklist.length ? 10 : 0 }}>
          {fields.map(f => (
            <div key={f.key} style={{ display:'flex', alignItems:'center', gap:6, padding:'5px 10px', background:'var(--color-card)', borderRadius:8, border:'1px solid var(--color-line)', fontSize:12 }}>
              <Dot color={f.color} />
              <span style={{ color:'var(--color-ink-muted)' }}>{f.label}:</span>
              <span style={{ fontWeight:600 }}>{Array.isArray(f.value) ? f.value.join(', ') : (f.value ?? '—')}</span>
            </div>
          ))}
        </div>
      )}

      {due_checklist.length > 0 && (
        <div>
          <div style={{ fontSize:10.5, fontWeight:700, color:'var(--color-ink-muted)', textTransform:'uppercase', marginBottom:6 }}>Due checklist</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
            {due_checklist.map(item => (
              <div key={item.label} style={{ display:'flex', alignItems:'center', gap:6, padding:'5px 10px', background:'var(--color-card)', borderRadius:8, border:'1px solid var(--color-line)', fontSize:12 }}>
                <Dot color={item.color} />
                <span>{item.label}</span>
                {item.last_done && <span style={{ color:'var(--color-ink-muted)' }}>({new Date(item.last_done).toLocaleDateString('en-IN')})</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
